import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { changeEventsModal, resetInvitationsPusher } from 'src/actions/event'
import { EMAIL_CONTACT_METHOD } from 'src/services/contact_methods'
import {
  eventSelector,
  invitationsPusherSelector,
  activeInvitationsSelector,
  isHostSelector,
  isHostOrCohostSelector,
  eventTicketCountsSelector,
  ticketTypesSelector,
  usersSelector,
  userSelector,
  hostSelector,
  hasPaidTicketsSelector,
  branchFloatingButtonLinkSelector,
  eventTokenSelector,
  invitationTokenSelector
} from 'src/selectors'
import {
  deleteInvitation,
  updateInvitation,
  invitations_activate,
  updateInvitationDelivery,
  acceptInvitationByHost,
  declineInvitationByHost
} from 'src/actions/invitations'

import { setInvitationsCount } from 'src/actions'
import { arrayDeduplicationWithObject } from 'services/utils'
import debounce from 'lodash/debounce'
import moment from 'moment'
import PopConfirm from 'components/common/PopConfirm/PopConfirm'
import { getPlanBySlug, PlanType } from 'src/services/stripe_checkout_configs'
import { changeVisible, setContent, setNeededCredits } from 'src/actions/globalLayer'
import { GLOBAL_LAYER_CONTENT } from 'src/constants'
import { Modal } from 'antd'
import { fullName } from 'src/services/user_helpers'
import { issuesTicketMutation, revokeTicketsMutation, listAllOrdersForEventRequest } from 'src/actions/paymentGraphql'
import { hostFullName } from 'src/services/event_helpers'
import DownloadAppModal from 'src/components/common/DownloadAppModal/DownloadAppModal'
import QRCodeDownloadAppModal from 'src/components/common/QRCodeDownloadAppModal/QRCodeDownloadAppModal'
import queryString from 'query-string'
import { isMobile } from 'src/services/browser_detection'

const WithGuestsListV2 = WrappedComponent => {
  return () => {
    const dispatch = useDispatch()
    const event = useSelector(eventSelector)
    const needPayTicket = useSelector(hasPaidTicketsSelector)
    const invitationsPusher = useSelector(invitationsPusherSelector)
    const activeInvitations = useSelector(activeInvitationsSelector)
    const eventTicketCounts = useSelector(eventTicketCountsSelector)
    const ticketTypes = useSelector(ticketTypesSelector)
    const users = useSelector(usersSelector)
    const isHost = useSelector(isHostSelector)
    const isHostOrCohost = useSelector(isHostOrCohostSelector)
    const user = useSelector(userSelector) || {}
    const host = useSelector(hostSelector)
    const branchLink = useSelector(branchFloatingButtonLinkSelector)
    const eventToken = useSelector(eventTokenSelector)
    const invitationToken = useSelector(invitationTokenSelector)

    const tags = ['all', 'yes', 'no', 'not replied', 'unsent']
    const [selectedGuest, setSelectedGuest] = useState(null)
    const selectedGuestRef = useRef(null)
    const [searchValue, setSearchValue] = useState('')
    const [searchGuestsList, setSearchGuestsList] = useState([])
    const [selectedTag, setSelectedTag] = useState('all')
    const [guestsList, setGuestsList] = useState([])
    const [isLimitEmailModalVisible, setIsLimitEmailModalVisible] = useState(false)
    const currentSub = user?.current_subscription
    const planType = getPlanBySlug(currentSub?.plan?.slug, 'planType')
    const [isDownloadAppModalVisible, setIsDownloadAppModalVisible] = useState(false)

    const params = {}
    if (eventToken) params.event_token = eventToken
    if (invitationToken) params.invitation_token = invitationToken
    const downloadUrl = Object.keys(params).length > 0
      ? `${branchLink}?${queryString.stringify(params)}`
      : branchLink

    useEffect(() => {
      if (invitationsPusher.length > 0) {
        initialGuestsList()
      }
    }, [invitationsPusher.length])

    useEffect(() => {
      handleSearchGuests()
    }, [guestsList, selectedTag])

    useEffect(() => {
      const invitationsCount = activeInvitations.filter(
        item => item.delivery_state !== 'pending'
      ).length

      dispatch(setInvitationsCount(invitationsCount))
      initialGuestsList()
    }, [activeInvitations])

    useEffect(() => {
      initialGuestsList(true)
    }, [])

    const buildLatestGuestsList = () => {
      let sentGuests = [...activeInvitations].sort((a, b) => {
        if (moment(a.created_at).isAfter(moment(b.created_at))) return -1
        return 1
      })

      const activeTicketTypes = ticketTypes.filter(item => !item.archived_at)
      const activeTypeIds = activeTicketTypes.map(item => item.id)

      if (eventTicketCounts.ticketing_enabled_at) {
        sentGuests = sentGuests.map(invitation => {
          const eventTicket = eventTicketCounts.invitations.find(item => item.id === invitation.id)
          const ticketCounts = eventTicket?.ticket_counts.filter(tc => {
            return activeTypeIds.includes(tc.ticket_type_id)
          }) || []

          if (eventTicket?.rsvp_state === 'accepted') {
            for (let ticket of ticketCounts) {
              const label = ticketTypes.find(
                ticketType => ticketType.id === ticket.ticket_type_id
              ).label
              if (label === 'adult') {
                invitation.guest_count = ticket.ticket_count
              }
              if (label === 'child') {
                invitation.child_count = ticket.ticket_count
              }
            }
          } else {
            invitation.guest_count = 0
            invitation.child_count = 0
          }

          return invitation
        })
      }

      return sentGuests
    }

    const getGuestsList = (tag = selectedTag) => {
      const latestGuestsList = buildLatestGuestsList()

      switch (tag) {
        case 'all':
          return latestGuestsList
        case 'yes':
          return latestGuestsList.filter(invitation => invitation.rsvp_state === 'accepted')
        case 'no':
          return latestGuestsList.filter(invitation => invitation.rsvp_state === 'declined')
        case 'unsent':
          return latestGuestsList.filter(invitation => invitation.delivery_state === 'pending')
        case 'created':
          return latestGuestsList.filter(i => i.delivery_state === 'pending' && i.created_at)
        case 'not replied':
          return latestGuestsList.filter(
            i => i.rsvp_state === 'undecided' && i.delivery_state !== 'pending'
          )
        case 'failed':
          return latestGuestsList.filter(
            i => i.delivery_state === 'delivery_failed' || i.delivery_state === 'delivery_aborted'
          )
        default:
          return latestGuestsList
      }
    }

    const searchGuestsDebounce = debounce(value => {
      const activeGuestsList = getGuestsList()

      const searchGuestsWithName = activeGuestsList.filter(({ first_name, last_name }) => {
        const fullName = first_name + ' ' + last_name
        return fullName?.toLowerCase()?.includes(value.toLowerCase())
      })

      const searchGuestsWithContactMethodDisplayName = activeGuestsList.filter(
        ({ contact_method_display_name }) => {
          return contact_method_display_name?.toLowerCase()?.includes(value.toLowerCase())
        }
      )

      const filterGuests = arrayDeduplicationWithObject(
        searchGuestsWithName.concat(searchGuestsWithContactMethodDisplayName),
        'id'
      )

      setSearchGuestsList(filterGuests)
    }, 300)

    const initialGuestsList = (isCheckUnsent = false) => {
      dispatch(resetInvitationsPusher())

      const latestGuestsList = buildLatestGuestsList()
      let currentSelectedTag = selectedTag

      const unSentInvitations = latestGuestsList.filter(item => item.delivery_state === 'pending')

      if (isCheckUnsent && unSentInvitations.length > 0) {
        currentSelectedTag = 'unsent'
      } else if (unSentInvitations.length === 0) {
        currentSelectedTag = currentSelectedTag === 'unsent' ? 'all' : currentSelectedTag
      }

      setGuestsList(latestGuestsList)
      setSelectedTag(currentSelectedTag)
    }

    const handleSearchGuests = (value = searchValue) => {
      setSearchValue(value)

      if (!value) {
        setSearchGuestsList([])
        return
      }

      searchGuestsDebounce(value)
    }

    const handleSelectTag = tag => {
      setSelectedTag(tag)
      setSearchValue('')
      setSearchGuestsList([])
    }

    const handleSelectGuest = (guest) => {
      selectedGuestRef.current = guest
      setSelectedGuest(guest)
    }

    const getCurrentSelectedGuest = () => {
      return selectedGuestRef.current || selectedGuest
    }

    const handleRemoveGuest = () => {
      const currentGuest = getCurrentSelectedGuest()

      if (!currentGuest) {
        console.warn('No guest selected for removal')
        return
      }

      dispatch(
        deleteInvitation({
          eventId: event.id,
          invitationId: currentGuest.id,
          onSuccessCallback: () => {
            selectedGuestRef.current = null
            setSelectedGuest(null)
            initialGuestsList()
          }
        })
      )
    }

    function getRevokeFreeTicketsIds(ticketCountsWithDelta, orders) {
      const listMyOrderFlatTickets = orders.flatMap(order => order.tickets)
      let revokeFreeTicketsIds = []
      ticketCountsWithDelta.forEach(tc => {
        if (tc.delta < 0) {
          const activeTickets = listMyOrderFlatTickets.filter(
            t => t.ticket_type_id === tc.id && t.state === 'active'
          )
          const needRevokeCount = Math.abs(tc.delta)
          revokeFreeTicketsIds = revokeFreeTicketsIds.concat(
            activeTickets.slice(0, needRevokeCount).map(t => t.id)
          )
        }
      })
      return revokeFreeTicketsIds
    }

    function getTicketCountsWithDelta(ticketCounts, selectedGuest, eventTicketCounts) {
      const guestTicketInfo = eventTicketCounts.invitations?.find(inv => inv.id === selectedGuest.id)
      const originalTicketCounts = guestTicketInfo?.ticket_counts || []
      return ticketCounts.map(tc => {
        const original = originalTicketCounts.find(item => item.ticket_type_id === tc.id)
        const prevCount = original ? original.ticket_count : 0
        return {
          ...tc,
          prevCount,
          delta: tc.count - prevCount
        }
      })
    }

    function handleTicketAndInvitation({
      ticketCountsWithDelta,
      orders,
      selectedGuest,
      dispatch,
      afterTicketActions
    }) {
      if (ticketCountsWithDelta.some(tc => tc.delta > 0)) {
        const orderLineItems = ticketCountsWithDelta.filter(tc => tc.delta > 0).map(item => ({
          ticket_type_id: item.id,
          count: item.delta
        }))
        dispatch(
          issuesTicketMutation(
            {
              invitation: { id: selectedGuest.id },
              order_line_items: orderLineItems
            },
            {},
            {
              onSuccessCallback: afterTicketActions
            }
          )
        )
      } else if (ticketCountsWithDelta.some(tc => tc.delta < 0)) {
        const revokeFreeTicketsIds = getRevokeFreeTicketsIds(ticketCountsWithDelta.filter(tc => tc.delta < 0), orders)
        dispatch(
          revokeTicketsMutation(
            revokeFreeTicketsIds,
            'guest_tickets',
            {},
            {
              onSuccessCallback: afterTicketActions
            }
          )
        )
      } else {
        afterTicketActions()
      }
    }

    const onEditGuest = (params) => {
      const currentGuest = getCurrentSelectedGuest()
      if (!currentGuest) {
        console.warn('No guest selected for editing')
        return
      }

      const { first_name, last_name, cohost, rsvpState, ticketCounts } = params

      let guestUser = users[currentGuest.guest]

      let resolvedFirstName = first_name ?? currentGuest.first_name
      let resolvedLastName = last_name ?? currentGuest.last_name
      if (resolvedFirstName === '' && resolvedLastName === '' && guestUser) {
        resolvedFirstName = guestUser.first_name
        resolvedLastName = guestUser.last_name
      }

      const invitation = {
        ...currentGuest,
        first_name: resolvedFirstName,
        last_name: resolvedLastName,
        cohost: typeof cohost === 'boolean' ? cohost : currentGuest.cohost,
        rsvp_state: rsvpState || currentGuest.rsvp_state,
        guest_count: ticketCounts?.find(tc => tc.label === 'adult')?.count || 0,
        child_count: ticketCounts?.find(tc => tc.label === 'child')?.count || 0
      }

      if (rsvpState === 'declined') {
        if (rsvpState !== currentGuest.rsvp_state) {
          dispatch(
            declineInvitationByHost(
              currentGuest,
              currentGuest.id,
              event.id
            )
          )
        }
        dispatch(
          updateInvitation(
            invitation,
            currentGuest.id,
            event.id,
            {},
            {
              onSuccessCallback: () => {
                selectedGuestRef.current = null
                setSelectedGuest(null)
                initialGuestsList()
              }
            }
          )
        )
      } else if (rsvpState === 'accepted') {
        dispatch(
          listAllOrdersForEventRequest(
            event.id,
            currentGuest.id,
            {},
            {
              onSuccessCallback: response => {
                const orders = response?.data?.list_all_orders_for_event?.orders || []
                const ticketCountsWithDelta = getTicketCountsWithDelta(ticketCounts, currentGuest, eventTicketCounts)

                const afterTicketActions = () => {
                  if (rsvpState !== currentGuest.rsvp_state) {
                    dispatch(
                      acceptInvitationByHost(
                        currentGuest,
                        currentGuest.id,
                        event.id
                      )
                    )
                  }

                  dispatch(
                    updateInvitation(
                      invitation,
                      currentGuest.id,
                      event.id,
                      {},
                      {
                        onSuccessCallback: () => {
                          selectedGuestRef.current = null
                          setSelectedGuest(null)
                          initialGuestsList()
                        }
                      }
                    )
                  )
                }

                handleTicketAndInvitation({
                  ticketCountsWithDelta,
                  orders,
                  selectedGuest: currentGuest,
                  dispatch,
                  afterTicketActions
                })
              }
            }
          )
        )
      } else {
        dispatch(
          updateInvitation(
            invitation,
            currentGuest.id,
            event.id,
            {},
            {
              onSuccessCallback: () => {
                selectedGuestRef.current = null
                setSelectedGuest(null)
                initialGuestsList()
              }
            }
          )
        )
      }
    }

    const handleSendInvitations = () => {
      window.mixpanel.track('INVITES Sent', { event_id: event.id, user_id: user.id })

      const emailInvitations = guestsList.filter(
        item => item.contact_method.type === EMAIL_CONTACT_METHOD
      )

      if (emailInvitations.length > 94) {
        if (planType === PlanType.InvitationMaker || planType === PlanType.Free) {
          setIsLimitEmailModalVisible(true)
          return
        }
      }

      dispatch(
        invitations_activate({
          eventId: event.id,
          eventName: event.name,
          hostName: hostFullName(event, host),
          onSuccessCallback: data => {
            const { estimated_cost, available_credit, sending_ids } = data
            if (sending_ids.length > 0) {
              dispatch(updateInvitationDelivery(sending_ids))
            }

            const hostInvitations = guestsList.filter(
              item => item.guest === host.id
            )

            const allUnsentInvitations = guestsList.filter(
              item => item.delivery_state === 'pending'
            )

            const isFirstInvitation = allUnsentInvitations.length === guestsList.length - hostInvitations.length

            if (sending_ids.length > 0 && isFirstInvitation) {
              if (window.mixpanel) {
                window.mixpanel.track('Guests Invited', { event_id: event.id, user_id: user.id })
              }
              setIsDownloadAppModalVisible(true)
            }

            if (available_credit < estimated_cost) {
              if (!isHost) {
                Modal.info({
                  title: 'SEND INVITES',
                  centered: true,
                  content: `Looks like your Host ${fullName(
                    host
                  )} is out of invites. Let them know they need to upgrade their plan in order to send more invites.`
                })
                return
              }
              dispatch(setNeededCredits(Number(estimated_cost) - Number(available_credit)))
              dispatch(setContent(GLOBAL_LAYER_CONTENT.unsentInvites))
              dispatch(changeVisible(true))
            }
          }
        })
      )
    }

    const handleSendHelpEmail = () => {
      window.location.href = 'mailto:help@hobnob.io'
      setIsLimitEmailModalVisible(false)
    }

    return (
      <>
        <WrappedComponent
          tags={tags}
          selectedTag={selectedTag}
          selectedGuest={selectedGuest}
          searchValue={searchValue}
          searchGuestsList={searchGuestsList}
          userId={user.id}
          event={event}
          users={users}
          onChangeEventsModal={tab => dispatch(changeEventsModal(tab))}
          isHost={isHost}
          isHostOrCohost={isHostOrCohost}
          ticketTypes={ticketTypes}
          eventTicketCounts={eventTicketCounts}
          needPayTicket={needPayTicket}
          onInputChange={handleSearchGuests}
          onGetGuestsList={getGuestsList}
          onSelectTag={handleSelectTag}
          onRemoveGuest={handleRemoveGuest}
          onEditGuest={onEditGuest}
          onSelectGuest={handleSelectGuest}
          onSendInvitations={handleSendInvitations}
        />

        <PopConfirm
          visible={isLimitEmailModalVisible}
          onClose={() => setIsLimitEmailModalVisible(false)}
          onConfirm={handleSendHelpEmail}
          confirmText="CONTACT SUPPORT"
          cancelText="No, thanks"
        >
          <div style={{ padding: '0 30px' }}>
            <h3>Contact Support</h3>
            <p style={{ fontSize: '15px', fontWeight: 400 }}>
              It seems you are trying to send a large number of email invites. To proceed, please contact Hobnob support. To continue sending by text only, you only need to have enough credits in your account.
            </p>
          </div>
        </PopConfirm>

        <DownloadAppModal
          show={isDownloadAppModalVisible && isMobile()}
          onClose={() => setIsDownloadAppModalVisible(false)}
          showAppleIcon={false}
          buttonText="Download Hobnob App"
          title="Get the app for RSVP notifications and a lot more!"
          href={downloadUrl}
        />

        <QRCodeDownloadAppModal
          show={isDownloadAppModalVisible && !isMobile()}
          onClose={() => setIsDownloadAppModalVisible(false)}
          title="Get the app for RSVP notifications and a lot more!"
          href={downloadUrl}
        />
      </>
    )
  }
}

export default WithGuestsListV2
